import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2, Plus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageTitle, Badge, ConfirmDelete } from "@/components/admin/ui";
import { Switch } from "@/components/ui/switch";
import { formatPKR, fromPrice } from "@/lib/format";

export default function VehiclesAdmin() {
  const qc = useQueryClient();
  const { data: vehicles = [], isLoading } = useQuery({ queryKey: ["admin-vehicles"], queryFn: async () => (await api.get("/admin/vehicles")).data, staleTime: 0 });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["admin-vehicles"] }); qc.invalidateQueries({ queryKey: ["vehicles"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); };

  const toggle = useMutation({
    mutationFn: async (v) => { const { id, created_at, updated_at, ...body } = v; return api.put(`/admin/vehicles/${id}`, { ...body, published: !v.published }); },
    onSuccess: (_, v) => { toast.success(v.published ? "Vehicle hidden from website" : "Vehicle published"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const remove = useMutation({
    mutationFn: async (id) => api.delete(`/admin/vehicles/${id}`),
    onSuccess: () => { toast.success("Vehicle deleted"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div data-testid="admin-vehicles-page">
      <PageTitle eyebrow="Inventory" title="Vehicles">
        <Link to="/admin/vehicles/new" className="btn-crimson !py-2.5" data-testid="add-vehicle-button"><Plus className="h-4 w-4" /> Add vehicle</Link>
      </PageTitle>
      {isLoading ? <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-crimson" /></div> : (
        <div className="admin-card overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm" data-testid="admin-vehicles-table">
            <thead className="border-b border-white/10 text-[11px] font-mono uppercase tracking-[0.16em] text-slate-500">
              <tr><th className="px-5 py-4 font-normal">Model</th><th className="px-5 py-4 font-normal">Category</th><th className="px-5 py-4 font-normal">From price</th><th className="px-5 py-4 font-normal">Order</th><th className="px-5 py-4 font-normal">Published</th><th className="px-5 py-4 font-normal text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {vehicles.map((v) => {
                const price = fromPrice(v);
                return (
                  <tr key={v.id} data-testid={`admin-vehicle-row-${v.slug}`} className="hover:bg-white/[0.02]">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-4">
                        <img src={v.hero_image} alt="" className="h-10 w-20 object-contain" />
                        <div><p className="font-display font-semibold">{v.name}</p><p className="font-mono text-[11px] text-slate-500">/{v.slug}</p></div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-300">{v.category}{v.badge && <span className="ml-2"><Badge tone="gold">{v.badge}</Badge></span>}</td>
                    <td className="px-5 py-3 font-display font-semibold">{price ? formatPKR(price) : <span className="text-slate-500">TBA</span>}</td>
                    <td className="px-5 py-3 font-mono text-slate-400">{v.order}</td>
                    <td className="px-5 py-3"><Switch checked={v.published} onCheckedChange={() => toggle.mutate(v)} data-testid={`vehicle-publish-toggle-${v.slug}`} /></td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2">
                        <Link to={`/admin/vehicles/${v.id}`} data-testid={`vehicle-edit-${v.slug}`} className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-slate-300 hover:border-white/40 hover:text-white"><Pencil className="h-4 w-4" /></Link>
                        <ConfirmDelete title={`Delete ${v.name}?`} description="The model will be removed from the website immediately." onConfirm={() => remove.mutate(v.id)} testid={`vehicle-delete-${v.slug}`}>
                          <button data-testid={`vehicle-delete-${v.slug}`} className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-slate-300 hover:border-crimson hover:text-crimson"><Trash2 className="h-4 w-4" /></button>
                        </ConfirmDelete>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!vehicles.length && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500">No vehicles yet. Add your first model.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
